import type { BundleAssignment, BundleStatus } from './bundle.types';

export type CounterStatus = BundleStatus;
export type CounterAssignment = BundleAssignment;

export type CounterType = 'TIMER' | 'STOCK';
export type CounterCountdownMode = 'global' | 'individual';
export type CounterEndAction = 'show_text' | 'redirect' | 'none' | 'hide' | 'restart';
export type CounterRestartMode = 'none' | 'after_end' | 'on_refresh' | 'browser_session';
export type CounterStockUnit = 'percent' | 'pieces';
export type CounterDepreciationMode = 'continuous' | 'scheduled';
export type CounterAppearance = 'light' | 'dark' | 'custom';
export type CounterWidthMode = 'full' | 'fit';
export type CounterTextAlign = 'left' | 'center' | 'right';
export type CounterInteractiveNotificationMode = 'time' | 'stock';
export type CounterInteractiveNotificationFrequency = 'rare' | 'standard' | 'frequent' | 'custom';
export type CounterInteractiveNotificationPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
export type CounterInteractiveNotificationTimeLabelMode = 'fixed' | 'random';
export type CounterPlacement =
  | 'summary_top'
  | 'after_title'
  | 'before_price'
  | 'after_price'
  | 'before_short_description'
  | 'after_short_description'
  | 'before_add_to_cart'
  | 'after_add_to_cart'
  | 'before_meta'
  | 'after_meta'
  | 'manual_only';

export interface CounterDuration {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface CounterTimerConfig {
  heading: string;
  showDays: boolean;
  showHours: boolean;
  labels: {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  };
  individualRestart: CounterRestartMode;
  globalRestartEnabled: boolean;
}

export interface CounterStockCycle {
  start: string;
  end: string;
}

export interface CounterStockConfig {
  heading: string;
  barLabel: string;
  unit: CounterStockUnit;
  stockTotal: number;
  stockStart: number;
  stockMin: number;
  depreciationEnabled: boolean;
  depreciationMode: CounterDepreciationMode;
  globalRestartEnabled: boolean;
  individualRestart: CounterRestartMode;
  nightMode: boolean;
  aggressive: {
    enabled: boolean;
    minutes: number;
    amount: number;
  };
  schedule: {
    weekdays: {
      mon: boolean;
      tue: boolean;
      wed: boolean;
      thu: boolean;
      fri: boolean;
      sat: boolean;
      sun: boolean;
    };
    cycles: CounterStockCycle[];
  };
}

export interface CounterInteractiveNotificationsConfig {
  enabled: boolean;
  mode: CounterInteractiveNotificationMode;
  frequency: CounterInteractiveNotificationFrequency;
  intervalMinSec: number;
  intervalMaxSec: number;
  firstDelaySec: number;
  displayDurationSec: number;
  maxPerSession: number;
  position: CounterInteractiveNotificationPosition;
  mobileEnabled: boolean;
  showVerified: boolean;
  showClose: boolean;
  messageTemplate: string;
  timeLabelMode: CounterInteractiveNotificationTimeLabelMode;
  timeLabel: string;
  timeMinSeconds: number;
  timeMaxSeconds: number;
  names: string;
  cities: string;
}

export interface CounterConfig {
  counterType: CounterType;
  placement: CounterPlacement;
  countdownMode: CounterCountdownMode;
  endTime: string | null;
  duration: CounterDuration;
  nextCycleDuration: CounterDuration;
  roundToTimeEnabled: boolean;
  roundToTime: string;
  endAction: CounterEndAction;
  endText: string;
  redirectUrl: string;
  cookiesLifetimeMinutes: number;
  hideMainOnEnd: boolean;
  resetVersion: number;
  timer: CounterTimerConfig;
  stock: CounterStockConfig;
  interactiveNotifications: CounterInteractiveNotificationsConfig;
}

const DEFAULT_NOTIFICATION_NAMES = [
  'Marcin',
  'Anna',
  'Katarzyna',
  'Piotr',
  'Michał',
  'Agnieszka',
  'Tomasz',
  'Karolina',
  'Paweł',
  'Magdalena',
  'Krzysztof',
  'Monika',
  'Łukasz',
  'Marta',
  'Mateusz',
  'Natalia',
  'Jakub',
  'Aleksandra',
  'Grzegorz',
  'Ewa',
  'Dawid',
  'Joanna',
  'Kamil',
  'Weronika',
  'Adam',
  'Sylwia',
  'Rafał',
  'Patrycja',
  'Bartosz',
  'Klaudia',
].join('\n');

const DEFAULT_NOTIFICATION_CITIES = [
  'Gdynia, Polska',
  'Rumia, Polska',
  'Reda, Polska',
  'Wejherowo, Polska',
  'Puck, Polska',
  'Kartuzy, Polska',
  'Kościerzyna, Polska',
  'Tczew, Polska',
  'Starogard Gdański, Polska',
  'Malbork, Polska',
  'Kwidzyn, Polska',
  'Lębork, Polska',
  'Słupsk, Polska',
  'Chojnice, Polska',
  'Bytów, Polska',
  'Żukowo, Polska',
  'Pruszcz Gdański, Polska',
  'Skarszewy, Polska',
  'Pelplin, Polska',
  'Gniew, Polska',
  'Sztum, Polska',
  'Nowy Dwór Gdański, Polska',
  'Władysławowo, Polska',
  'Hel, Polska',
  'Jastarnia, Polska',
  'Ustka, Polska',
  'Darłowo, Polska',
  'Świdwin, Polska',
  'Drawsko Pomorskie, Polska',
  'Miastko, Polska',
  'Szczecinek, Polska',
  'Złotów, Polska',
  'Wałcz, Polska',
  'Piła, Polska',
  'Człuchów, Polska',
  'Czersk, Polska',
  'Tuchola, Polska',
  'Brodnica, Polska',
  'Chełmno, Polska',
  'Grudziądz, Polska',
  'Inowrocław, Polska',
  'Żnin, Polska',
  'Wągrowiec, Polska',
  'Oborniki, Polska',
].join('\n');

export interface CounterStyle {
  appearance: CounterAppearance;
  fontFamily: string;
  widthMode: CounterWidthMode;
  textAlign: CounterTextAlign;
  animationsEnabled: boolean;
  minWidthEnabled: boolean;
  minWidth: number;
  spacing: {
    marginTop: number;
    marginBottom: number;
    paddingX: number;
    paddingY: number;
    gap: number;
  };
  colors: {
    background: string;
    text: string;
    mutedText: string;
    accent: string;
    border: string;
    low: string;
    lowPulse: string;
    medium: string;
    high: string;
    track: string;
  };
  typography: {
    headingSize: number;
    headingWeight: number;
    valueSize: number;
    valueWeight: number;
    labelSize: number;
    labelWeight: number;
  };
  backgroundImage: {
    url: string;
    position: string;
    opacity: number;
  };
  borderRadius: number;
  borderWidth: number;
}

export interface CounterModel {
  id: string;
  tenantId: string;
  integrationId: string;
  name: string;
  slug: string;
  status: CounterStatus;
  assignmentType: CounterAssignment;
  productIds: number[];
  categoryIds: number[];
  config: CounterConfig;
  style: CounterStyle;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const COUNTER_TYPE_LABELS: Record<CounterType, string> = {
  TIMER: 'Hurry Up',
  STOCK: 'Hurry Stock',
};

export const COUNTER_END_ACTION_LABELS: Record<CounterEndAction, string> = {
  show_text: 'Pokaż tekst po końcu',
  redirect: 'Przekieruj',
  none: 'Nic nie rób',
  hide: 'Ukryj licznik',
  restart: 'Restart licznika',
};

export const COUNTER_RESTART_LABELS: Record<CounterRestartMode, string> = {
  none: 'Nie restartuj',
  after_end: 'Restart po zakończeniu',
  on_refresh: 'Restart po odświeżeniu strony',
  browser_session: 'Restart po ponownym otwarciu przeglądarki',
};

export const COUNTER_PLACEMENT_LABELS: Record<CounterPlacement, string> = {
  summary_top: 'Na początku sekcji produktu',
  after_title: 'Pod tytułem produktu',
  before_price: 'Nad ceną',
  after_price: 'Pod ceną',
  before_short_description: 'Nad krótkim opisem',
  after_short_description: 'Pod krótkim opisem',
  before_add_to_cart: 'Nad call to action',
  after_add_to_cart: 'Pod call to action',
  before_meta: 'Nad meta produktu',
  after_meta: 'Pod meta produktu',
  manual_only: 'Tylko shortcode',
};

export const DEFAULT_COUNTER_CONFIG: CounterConfig = {
  counterType: 'TIMER',
  placement: 'before_add_to_cart',
  countdownMode: 'global',
  endTime: null,
  duration: { days: 1, hours: 0, minutes: 0, seconds: 0 },
  nextCycleDuration: { days: 1, hours: 0, minutes: 0, seconds: 0 },
  roundToTimeEnabled: false,
  roundToTime: '00:00',
  endAction: 'show_text',
  endText: 'Koniec',
  redirectUrl: '',
  cookiesLifetimeMinutes: 5256000,
  hideMainOnEnd: false,
  resetVersion: 0,
  timer: {
    heading: 'PROMOCJA KOŃCZY SIĘ ZA',
    showDays: true,
    showHours: true,
    labels: {
      days: 'DNI',
      hours: 'GODZIN',
      minutes: 'MINUT',
      seconds: 'SEKUND',
    },
    individualRestart: 'after_end',
    globalRestartEnabled: false,
  },
  stock: {
    heading: 'Stan magazynowy produktu',
    barLabel: 'Pozostało',
    unit: 'percent',
    stockTotal: 100,
    stockStart: 20,
    stockMin: 1,
    depreciationEnabled: true,
    depreciationMode: 'continuous',
    globalRestartEnabled: false,
    individualRestart: 'after_end',
    nightMode: false,
    aggressive: {
      enabled: false,
      minutes: 1,
      amount: 0,
    },
    schedule: {
      weekdays: {
        mon: true,
        tue: true,
        wed: true,
        thu: true,
        fri: true,
        sat: true,
        sun: true,
      },
      cycles: [
        { start: '09:00', end: '12:00' },
      ],
    },
  },
  interactiveNotifications: {
    enabled: false,
    mode: 'time',
    frequency: 'standard',
    intervalMinSec: 28,
    intervalMaxSec: 45,
    firstDelaySec: 5,
    displayDurationSec: 6,
    maxPerSession: 8,
    position: 'bottom-left',
    mobileEnabled: true,
    showVerified: true,
    showClose: true,
    messageTemplate: 'Zamówił(a) {product}',
    timeLabelMode: 'random',
    timeLabel: '12 min temu',
    timeMinSeconds: 10,
    timeMaxSeconds: 120,
    names: DEFAULT_NOTIFICATION_NAMES,
    cities: DEFAULT_NOTIFICATION_CITIES,
  },
};

export const DEFAULT_COUNTER_STYLE: CounterStyle = {
  appearance: 'light',
  fontFamily: '',
  widthMode: 'full',
  textAlign: 'center',
  animationsEnabled: true,
  minWidthEnabled: false,
  minWidth: 200,
  spacing: {
    marginTop: 12,
    marginBottom: 12,
    paddingX: 16,
    paddingY: 14,
    gap: 8,
  },
  colors: {
    background: '#f5f5f6',
    text: '#111827',
    mutedText: '#6b7280',
    accent: '#ea4d6f',
    border: '#e5e7eb',
    low: '#fd4470',
    lowPulse: '#ff8ca7',
    medium: '#eac131',
    high: '#00bf6d',
    track: '#ffffff',
  },
  typography: {
    headingSize: 14,
    headingWeight: 700,
    valueSize: 36,
    valueWeight: 700,
    labelSize: 11,
    labelWeight: 600,
  },
  backgroundImage: {
    url: '',
    position: 'center',
    opacity: 1,
  },
  borderRadius: 8,
  borderWidth: 1,
};
